import React, {
  FC,
  forwardRef,
  ForwardRefRenderFunction,
  PropsWithChildren,
  ReactNode,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Drawer,
  Scrollbar,
  Spin,
  Switch,
  Table,
} from '@bedrock/components';
import { ButtonProps } from '@bedrock/components/lib/Button';
import { DrawerProps } from '@bedrock/components/lib/Drawer';
import { ColumnType } from '@bedrock/components/lib/Table';
import CooperationDetails from '@mpc/common/src/features/CooperationFormList/CooperationDetails';
import { mergeRowSpanByKeys } from '#components/ProTablePlusForm/addGroupTable';
import { useMemoizedFn, useRequest } from 'ahooks';
import classNames from 'classnames';
import { omit } from 'lodash';
import {
  COLUMN_CONFIG,
  COLUMN_WIDTH_BY_DATA_TYPE,
  QuoteDetailColVo,
  QuoteDetailDiffType,
  QuoteDetailValue,
  QuoteDetailVo,
  getColumnsVo,
  getQuoteDetailValue,
  getQuotesVo,
  getStandardCodes,
  getDisplayValue,
} from './utils';
import { $sType } from '#utils';
import {
  demandDiffSupplierQuote,
  demandStandardCatalogListByCodeList,
  DemandStandardCatalogVo,
  DemandSupplierQuoteDiffVo,
  JournalDemandDetailsResDtoCooperateAccountDto,
  outsourceDemandDiffSupplierQuote,
  outsourceDemandStandardCatalogListByCodeList,
} from '#services/api';
import './index.less';
import TextAreaWithUpload from '#components/SForm/SFormElement/TextAreaWithUpload';

const CLS_PREFIX = 'quote-detail-show';

// Content

interface QuoteDetailShowProps {
  blobId: number;
  canShowDiff?: boolean;
  cooperateAccounts?: JournalDemandDetailsResDtoCooperateAccountDto[];
  demandId: number;
  diffLabel?: React.ReactNode;
  supplierId: number;
  cooperateAccountId?: number;
}

const QuoteDetailShow: FC<QuoteDetailShowProps> = ({
  blobId,
  canShowDiff = true,
  cooperateAccounts,
  demandId,
  diffLabel = '显示供应商变更差异',
  supplierId,
  cooperateAccountId,
}) => {
  const [showDiff, setShowDiff] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const cooperateAccountsMap = useMemo(() => {
    return cooperateAccounts?.reduce((prev, curr) => {
      prev[curr.id] = curr;
      return prev;
    }, {} as Record<number, JournalDemandDetailsResDtoCooperateAccountDto>);
  }, [cooperateAccounts]);

  const { loading, data: quotesData } = useRequest(
    () => {
      const handleResData = (data?: DemandSupplierQuoteDiffVo) => {
        const columnsData = getColumnsVo(data?.columns);
        const standardCodes = Array.from(
          new Set([
            ...getStandardCodes(data?.quote_data),
            ...getStandardCodes(data?.diff_quote_data),
          ])
        );

        return {
          columnsData,
          quoteData: getQuotesVo(columnsData, data?.quote_data, {
            enableStandardColumnMerge: true,
          }),
          diffQuoteData: getQuotesVo(columnsData, data?.diff_quote_data, {
            enableStandardColumnMerge: false,
          }),
          standardCodes,
        };
      };

      if ($sType === 'outer') {
        return outsourceDemandDiffSupplierQuote({
          demand_id: demandId,
          demand_supplier_id: supplierId,
          cooperate_account_id: cooperateAccountId,
          blob_id: blobId,
          diff: showDiff,
        }).then((res) => handleResData(res?.data));
      }

      return demandDiffSupplierQuote({
        demand_id: demandId,
        demand_supplier_id: supplierId,
        cooperate_account_id: cooperateAccountId,
        blob_id: blobId,
        diff: showDiff,
      }).then((res) => handleResData(res?.data));
    },
    { refreshDeps: [showDiff] }
  );

  const { data: standardOptions } = useRequest(
    () => {
      const request =
        $sType === 'outer'
          ? outsourceDemandStandardCatalogListByCodeList
          : demandStandardCatalogListByCodeList;

      return request({
        standard_catalog_code_list: quotesData?.standardCodes,
      }).then((res) => {
        return {
          arr: res?.data,
          map: res.data?.reduce((prev, curr) => {
            prev[curr.code] = curr;
            return prev;
          }, {} as Record<string, DemandStandardCatalogVo>),
        };
      });
    },
    {
      ready: !!quotesData?.standardCodes?.length,
      refreshDeps: [quotesData?.standardCodes],
    }
  );

  const columns = useMemo(() => {
    const handleOneColumn = (col: QuoteDetailColVo) => {
      if (col.children?.length) {
        return {
          dataIndex: col.code,
          title: col.label,
          align: 'left',
          children: col.children.map((child) => handleOneColumn(child)),
        } as ColumnType<QuoteDetailVo>;
      }

      return {
        title: col.label,
        width: 120,
        align: 'left',
        ...COLUMN_WIDTH_BY_DATA_TYPE[col.data_type],
        ...COLUMN_CONFIG[col.code],
        dataIndex: col.code,
        render: (value: QuoteDetailValue, record, index) => {
          let children: ReactNode = getDisplayValue(
            col.code,
            getQuoteDetailValue(value, col),
            { standardOptions: standardOptions?.map }
          );
          let rowSpan = 1;

          if (showDiff && !Array.isArray(value) && typeof value === 'object') {
            if (value.diffType === QuoteDetailDiffType.MODIFIED) {
              children = (
                <div className="diff-modified-cell">
                  <div>
                    {getDisplayValue(col.code, value.supplierValue, {
                      standardOptions: standardOptions?.map,
                    })}
                  </div>
                  <div>
                    {getDisplayValue(col.code, value.prefillValue, {
                      standardOptions: standardOptions?.map,
                    })}
                  </div>
                </div>
              );
            } else if (value.diffType === QuoteDetailDiffType.CREATED) {
              children = (
                <div className="diff-created-cell">
                  <div>
                    {getDisplayValue(col.code, value.supplierValue, {
                      standardOptions: standardOptions?.map,
                    })}
                  </div>
                </div>
              );
            } else if (value.diffType === QuoteDetailDiffType.DELETED) {
              children = (
                <div className="diff-deleted-cell">
                  <div>
                    {getDisplayValue(col.code, value.prefillValue, {
                      standardOptions: standardOptions?.map,
                    })}
                  </div>
                </div>
              );
            }
          }

          const quoteKeySpan = mergeRowSpanByKeys(['quoteKey'], index, quotesData?.quoteData);
          if (col.code === 'cooperateAccountId') {
            children = (
              <CooperationDetails
                index_id={
                  cooperateAccountsMap?.[record.cooperateAccountId]
                    ?.index_id ?? (index + 1)
                }
                showValuesTitle
                moreButtonProps={{ size: 'xSmall' }}
                ellipsisValue
                value={
                  cooperateAccountsMap?.[record.cooperateAccountId]
                    ?.property_values
                }
              />
            );
            rowSpan = mergeRowSpanByKeys(['cooperateAccountId'], index, quotesData?.quoteData);
          } else if (col.code === 'standardCode1') {
            rowSpan = mergeRowSpanByKeys(['cooperateAccountId','standardCode1'], index, quotesData?.quoteData);
          } else if (col.code === 'standardCode2') {
            rowSpan = mergeRowSpanByKeys(['cooperateAccountId','standardCode2'], index, quotesData?.quoteData);
          } else if (col.code === 'standardCode3') {
            rowSpan = mergeRowSpanByKeys(['cooperateAccountId','standardCode3'], index, quotesData?.quoteData);
          } else {
            if (!showDiff && !col.code?.startsWith('l2$')) {
              rowSpan = quoteKeySpan
            }
          }

          if (col.code === 'l2$index') {
            children = record?.l2$index ? +record?.l2$index + 1 : 1;
            if (Number.isNaN(children)) {
              children = 1;
            }
          }

          if (col.data_type === 'text_with_upload') {
            children = <TextAreaWithUpload value={value} view />;
          }

          return { children, props: { rowSpan } };
        },
      } as ColumnType<QuoteDetailVo>;
    };

    return (quotesData?.columnsData?.map((qeh) => handleOneColumn(qeh)) ??
      []) as ColumnType<QuoteDetailVo>[];
  }, [
    cooperateAccountsMap,
    quotesData?.columnsData,
    showDiff,
    standardOptions,
  ]);

  const showData = useMemo(() => {
    // 根据cooperateAccountsMap，过滤quotesData
    const list = showDiff ? quotesData?.diffQuoteData : quotesData?.quoteData;
    const newList = list?.filter((item) => {
      return cooperateAccountsMap?.[item.cooperateAccountId];
    });
    return newList ?? [];
  }, [showDiff, quotesData, cooperateAccountsMap]);

  return (
    <div ref={wrapperRef} className={CLS_PREFIX}>
      {canShowDiff && (
        <div className={`${CLS_PREFIX}-header`}>
          <div className={`${CLS_PREFIX}-switch`}>
            <span>{diffLabel}</span>
            <Switch value={showDiff} onChange={setShowDiff} />
          </div>
          <div className={`${CLS_PREFIX}-diff`}>
            <span>
              <span style={{ background: 'var(--o50)' }} />
              <span>变更</span>
            </span>
            <span>
              <span style={{ background: 'var(--g50)' }} />
              <span>新增</span>
            </span>
            <span>
              <span style={{ background: 'var(--text-color-4)' }} />
              <span
                style={{
                  color: 'var(--text-color-3)',
                  textDecoration: 'line-through',
                }}
              >
                删除
              </span>
            </span>
          </div>
        </div>
      )}
      {!loading && (
        <Table
          columns={columns}
          data={showData}
          rowKey="_id"
          size="small"
          scroll={{ x: true }}
          verticalBorder
          hoverColor={false}
          sticky={{
            offsetScroll: -16,
            getContainer: () =>
              wrapperRef?.current?.closest(
                '.rock-scrollbar-view'
              ) as HTMLElement,
          }}
          isShowLoading={loading}
        />
      )}
      {loading && <Spin.Global />}
    </div>
  );
};

// Drawer

interface QuoteDetailShowDrawerOpenParams extends QuoteDetailShowProps {
  drawerProps?: DrawerProps;
}

export interface QuoteDetailShowDrawerRef {
  open: (params?: QuoteDetailShowDrawerOpenParams) => void;
}

const QuoteDetailShowDrawerComp: ForwardRefRenderFunction<
  QuoteDetailShowDrawerRef
> = (props, ref) => {
  const [visible, setVisible] = useState(false);
  const [openParams, setOpenParams] =
    useState<QuoteDetailShowDrawerOpenParams>();

  const quoteDetailShowProps = useMemo(
    () => omit(openParams, ['drawerProps']),
    [openParams]
  );

  const open = useMemoizedFn((params) => {
    setVisible(true);
    setOpenParams(params);
  });

  const close = useMemoizedFn(() => {
    setVisible(false);
    setTimeout(() => {
      setOpenParams(undefined);
    }, 300);
  });

  useImperativeHandle(ref, () => ({ open }), []);

  return (
    <Drawer
      destroyOnClose
      height={window.innerHeight}
      maskClosable={false}
      title="报价详情"
      {...openParams?.drawerProps}
      className={classNames(
        `${CLS_PREFIX}-drawer`,
        openParams?.drawerProps?.className
      )}
      placement="bottom"
      visible={visible}
      onClose={close}
    >
      <Scrollbar>
        <QuoteDetailShow {...quoteDetailShowProps} />
      </Scrollbar>
    </Drawer>
  );
};

export const QuoteDetailShowDrawer = forwardRef(QuoteDetailShowDrawerComp);

// Button

interface QuoteDetailShowButtonProps
  extends PropsWithChildren,
    QuoteDetailShowDrawerOpenParams {
  buttonProps?: ButtonProps;
}

export const QuoteDetailShowButton: FC<QuoteDetailShowButtonProps> = ({
  children,
  buttonProps,
  ...drawerOpenParams
}) => {
  const quoteDetailShowDrawerRef = React.useRef<QuoteDetailShowDrawerRef>(null);

  const onClick = useMemoizedFn(() => {
    quoteDetailShowDrawerRef.current?.open(drawerOpenParams);
  });

  return (
    <>
      <Button type="link" {...buttonProps} onClick={onClick}>
        {children ?? '查看详情'}
      </Button>
      <QuoteDetailShowDrawer ref={quoteDetailShowDrawerRef} />
    </>
  );
};
